import { EntitySelector } from "@/components/EntitySelector/EntitySelector";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  getViewportDocumentCollisionBoundary,
  VIEWPORT_POPOVER_COLLISION_PADDING,
} from "@/constants/viewportPopoverCollision";
import { useConnectorEntitySelect } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/connectors/useConnectorEntitySelect";
import { RailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRow";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  ObjectActionMenu,
  useConnectorObjectActionHandlers,
  useObjectActions,
  type ObjectKind,
} from "@/object-actions";
import { cn } from "@/utils";
import {
  RESOURCE_TAG_FAVORITE,
  type IntegrationConnection,
} from "@cohesive/api-client";
import { IntegrationIcon } from "@cohesive/ui-components";
import { Blocks } from "lucide-react";
import { useState } from "react";

const CONNECTOR_KIND: ObjectKind = { type: "connector" };

export function ConnectorsRailConnectorRow({
  connection,
  dragRef,
  dragProps,
  isDragging,
}: {
  connection: IntegrationConnection;
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const menu = useRailRowContextMenu();
  const handleEntitySelect = useConnectorEntitySelect();

  const isFavorite = !!connection.tags?.includes(RESOURCE_TAG_FAVORITE);
  const handlers = useConnectorObjectActionHandlers(connection);
  const { actions } = useObjectActions({
    kind: CONNECTOR_KIND,
    surface: "left-rail",
    enabled: menu.open,
    state: { isFavorite },
    handlers,
  });

  return (
    <div
      ref={dragRef}
      onContextMenu={menu.onContextMenu}
      className={cn(
        "group/row relative",
        dragProps && "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50",
      )}
      {...dragProps}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <RailRow
            icon={Blocks}
            label={connection.integration_name}
            active={open}
            className="pr-8"
            leading={
              <IntegrationIcon
                platform={connection.integration_platform}
                name={connection.integration_name}
                imageURL={connection.integration_image_url}
                className="size-4 shrink-0 rounded object-cover"
              />
            }
          />
        </PopoverTrigger>
        <PopoverContent
          side="right"
          align="start"
          sideOffset={8}
          collisionBoundary={getViewportDocumentCollisionBoundary()}
          collisionPadding={VIEWPORT_POPOVER_COLLISION_PADDING}
          className={cn(
            "h-[520px] w-[min(700px,calc(100vw-2rem))] p-0 overflow-hidden",
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <EntitySelector
            connection={connection}
            onClose={() => setOpen(false)}
            onSelect={(entity) => {
              handleEntitySelect(entity);
              setOpen(false);
            }}
            className="h-full"
          />
        </PopoverContent>
      </Popover>

      <ObjectActionMenu
        actions={actions}
        kind={CONNECTOR_KIND}
        name={connection.integration_name}
        menuController={menu}
        label={connection.integration_name}
      />
    </div>
  );
}
